import { ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { IncomeCallService } from 'src/app/_Common/_Services/income-call.service';
import { UserService } from 'src/app/_Common/_Services/user.service';
import { IgnosisTransferService } from './ignosis-transfer.service';

const OFFER_MS = 5 * 60 * 1000;

type Invite = {
  url: string;
  frameOrigin: string;
  roomName: string;
  token: string;
  reason: string | null;
  dueId: number | null;
};

@Component({
  selector: 'mt-ignosis-transfer',
  templateUrl: './ignosis-transfer.component.html',
  styleUrls: ['./ignosis-transfer.component.scss'],
  standalone: false
})
export class IgnosisTransferComponent implements OnInit, OnDestroy {
  @ViewChild('frame') frame?: ElementRef<HTMLIFrameElement>;

  invite: Invite | null = null;
  preview = true;
  frameUrl: SafeResourceUrl | null = null;
  state: 'offered' | 'joined' | 'ended' | 'error' = 'offered';
  notice = '';
  private sentToken = '';
  private tabId = Math.random().toString(36).slice(2);
  private timer: ReturnType<typeof setTimeout> | null = null;
  private subscriptions: Subscription[] = [];

  constructor(
    private incomeCall: IncomeCallService,
    private userService: UserService,
    private transfer: IgnosisTransferService,
    private sanitizer: DomSanitizer,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    const held = localStorage.getItem('ignosis-transfer-tab');
    if (held && !held.startsWith(this.tabId + ':')) localStorage.removeItem('ignosis-transfer-tab');
    this.incomeCall.intializeIncomeSocket();
    const join = () => {
      const userId = this.transfer.sessionUserId$.value || this.userService.getLoggedInUserId();
      if (userId) this.incomeCall.joinIgnosisRoom('ignosis-' + String(userId));
    };
    join();
    const joinTimer = setInterval(join, 2000);
    this.subscriptions.push(this.transfer.sessionUserId$.subscribe(() => join()));
    this.subscriptions.push(new Subscription(() => clearInterval(joinTimer)));
    this.subscriptions.push(
      this.incomeCall.onIgnosisTransferOffer().subscribe((data) => {
        this.zone.run(() => this.onOffer(data));
      })
    );
    window.addEventListener('message', this.onMessage);
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.onMessage);
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.clearTimer();
  }

  closePreview() {
    this.preview = false;
  }

  leave() {
    this.postToFrame(this.state === 'joined' ? 'humanCalling:hangup' : 'humanCalling:hangup');
    this.finish('ended', this.state === 'joined' ? '' : '');
  }

  decline() {
    this.postToFrame('humanCalling:hangup');
    this.finish('ended', '');
  }

  private onOffer(data: any) {
    localStorage.removeItem('ignosis-transfer-tab');
    const invite = this.readInvite(data);
    if (!invite) return;
    this.claimTab(invite.roomName);
    const sameFrame = this.invite && this.invite.url === invite.url && this.frameUrl;
    this.invite = invite;
    this.preview = false;
    this.notice = '';
    this.state = 'offered';
    if (!sameFrame) {
      this.sentToken = '';
      this.frameUrl = this.sanitizer.bypassSecurityTrustResourceUrl(invite.url);
    } else if (this.sentToken && this.sentToken !== invite.token) {
      this.sentToken = '';
      this.sendInvite();
    }
    this.armTimer();
    this.cdr.detectChanges();
  }

  private readInvite(data: any): Invite | null {
    if (!data || !data.url || !data.roomName || !data.token) return null;
    const frameOrigin = originOf(data.url);
    if (!frameOrigin) return null;
    return {
      url: data.url,
      frameOrigin,
      roomName: data.roomName,
      token: data.token,
      reason: data.reason || null,
      dueId: data.dueId || null
    };
  }

  private onMessage = (event: MessageEvent) => {
    if (!this.invite || event.origin !== this.invite.frameOrigin) return;
    const data = event.data as { event?: string; message?: string } | null;
    if (!data || typeof data.event !== 'string') return;
    this.zone.run(() => {
      if (data.event === 'humanCalling:ready') {
        this.sendInvite();
        return;
      }
      if (data.event === 'humanCalling:joined') {
        this.state = 'joined';
        this.clearTimer();
        this.transfer.joined(this.invite!.roomName);
        return;
      }
      if (data.event === 'humanCalling:ended') {
        this.finish('ended', 'The customer left the call.');
        return;
      }
      if (data.event === 'humanCalling:error') {
        this.finish('error', data.message || 'The transfer could not be joined.');
      }
    });
  };

  private sendInvite() {
    if (!this.invite || !this.frame || this.sentToken === this.invite.token) return;
    this.frame.nativeElement.contentWindow?.postMessage(
      {
        event: 'humanCalling:invite',
        invite: {
          token: this.invite.token,
          roomName: this.invite.roomName,
          reason: this.invite.reason
        }
      },
      this.invite.frameOrigin
    );
    this.sentToken = this.invite.token;
  }

  private postToFrame(event: string) {
    if (!this.invite || !this.frame) return;
    this.frame.nativeElement.contentWindow?.postMessage({ event }, this.invite.frameOrigin);
  }

  private finish(state: 'ended' | 'error', notice: string) {
    const roomName = this.invite?.roomName;
    this.clearTimer();
    if (roomName) {
      this.transfer.release(roomName);
      this.releaseTab(roomName);
    }
    this.invite = null;
    this.frameUrl = null;
    this.sentToken = '';
    this.state = state;
    this.notice = notice;
    if (notice) {
      setTimeout(() => {
        if (!this.invite) this.notice = '';
      }, 4000);
    }
  }

  private armTimer() {
    this.clearTimer();
    this.timer = setTimeout(() => {
      if (this.state === 'offered') this.decline();
    }, OFFER_MS);
  }

  private clearTimer() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private claimTab(roomName: string) {
    localStorage.setItem('ignosis-transfer-tab', this.tabId + ':' + roomName);
  }

  private releaseTab(roomName: string) {
    const key = 'ignosis-transfer-tab';
    if (localStorage.getItem(key) === this.tabId + ':' + roomName) localStorage.removeItem(key);
  }
}

function originOf(url: string) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.origin : '';
  } catch (e) {
    return '';
  }
}
